import React, { useCallback, useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { RegistrationFormView } from './RegistrationFormView';
import { useFormik } from 'formik';
import { registrationProValidationSchema, registrationValidationSchema, siv } from './validationSchema';
import { useDeposit } from 'providers';
import { ClassifiedsService } from 'api';
import { Loader } from '../Loader/Loader';

interface Props {
  isPro: boolean;
  defaultErrorMessage?: string;
  customerReference?: string;
}

export const RegistrationForm: React.FC<Props> = ({ isPro, defaultErrorMessage, customerReference }) => {
  const { validateImmatOrVin, vehicle } = useDeposit();
  const [errorMessage, setErrorMessage] = useState<string>(defaultErrorMessage || '');
  const [vinError, setVinError] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [parkingFull, setParkingFull] = useState<boolean>(false);
  const { values, touched, handleSubmit, handleChange, isSubmitting, errors, setFieldValue } = useFormik({
    initialValues: {
      immatriculation: vehicle.registration || '',
      vin: (!isPro ? vehicle.vin?.slice(-3) : vehicle.vin) || '',
      autorisation: false,
    },
    validationSchema: !isPro ? registrationValidationSchema : registrationProValidationSchema,
    onSubmit: ({ immatriculation, vin, autorisation }, { setErrors, setSubmitting }) => {
      const errors: { immatriculation?: any; vin?: any; autorisation?: any } = {};

      if (immatriculation && !immatriculation.match(siv)) {
        errors.immatriculation =
          'Veuillez renseigner une immatriculation valide. Celle-ci ne doit contenir que des chiffres et des lettres.';
        // Sentry.captureMessage(`Immatriculation invalide: ${immatriculation}`);
      }

      if (isPro) {
        if (!vin && !immatriculation) {
          errors.immatriculation = 'Immatriculation obligatoire';
          errors.vin = 'Vin obligatoire';
        }

        if (vin && !(vin.length > 9)) {
          errors.vin = 'VIN incomplet';
        }
        if (!autorisation) {
          errors.autorisation = 'Veuillez accepter les informations légales';
        }
      }

      if (Object.keys(errors).length !== 0) {
        setErrors(errors);
        setSubmitting(false);
        return;
      }

      return validateImmatOrVin(immatriculation, vin)
        .catch((error: Error) => {
          if (error.message === 'error_vin') {
            setVinError(true);
          } else {
            setErrorMessage(error.message);
          }
        })
        .finally(() => setSubmitting(false));
    },
  });

  useEffect(() => {
    if (defaultErrorMessage) {
      setErrorMessage(defaultErrorMessage);
    }
  }, [defaultErrorMessage]);

  useEffect(() => {
    if (isPro) {
      ClassifiedsService.isParkingFull(customerReference!.toUpperCase())
        .then(parkingFull => {
          if (parkingFull) {
            setParkingFull(true);
            setErrorMessage('Dépôt impossible, vous avez atteint la limite de votre parking');
          }
        })
        .catch(r => {
          setErrorMessage(r.message);
          setParkingFull(true);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [isPro, customerReference]);

  const handleVinErrorClick = useCallback(() => {
    Sentry.captureMessage('Vin error email assistance clicked');
  }, []);

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <RegistrationFormView
          immatriculation={values.immatriculation}
          vin={values.vin}
          vinError={vinError}
          onVinErrorClick={handleVinErrorClick}
          isPro={isPro}
          autorisation={values.autorisation}
          handleSubmit={handleSubmit}
          handleChange={handleChange}
          setFieldValue={setFieldValue}
          isSubmitting={isSubmitting}
          errors={errors}
          errorMessage={errorMessage}
          touched={touched}
          parkingFull={parkingFull}
        />
      )}
    </>
  );
};
